// Share / Export for every list in the app — one PDF and one Excel builder that
// any page feeds with the columns it already renders on screen, so a report
// never needs its own exporter (the same idea as SharpAccounting's
// ReportShareDialog, rebuilt for this app's plain-CSS UI).
//
// A page passes:
//   title     "Guests"                      - the report's name
//   subtitle  "Sahil weds Diksha · Wedding" - event / filter line under it
//   columns   [{ key, label, align?, width?, render? }]
//   rows      the array it is already showing
//   totalsRow optional { [key]: value } printed as a bold last line
//
// `render` may return JSX (the screen's chips and links): cellText() walks it
// down to plain text, so a page never has to keep a second set of columns.
//
// jsPDF / xlsx are ~1 MB together and most sessions never export, so both are
// loaded with import() the first time someone opens the dialog.

export const PDF_MIME = 'application/pdf';
export const EXCEL_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

// ── A cell's plain text, whatever the column renders on screen ──────────────
const nodeText = (node) => {
  if (node === null || node === undefined || node === false || node === true) return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(nodeText).join('');
  if (node.props) {
    if (node.props.children !== undefined) return nodeText(node.props.children);
    if (node.props.value !== undefined) return nodeText(node.props.value);
  }
  return '';
};

export const cellText = (col, row) => {
  if (col.exportValue) return String(col.exportValue(row) ?? '');
  const raw = col.render ? col.render(row) : row[col.key];
  const text = nodeText(raw);
  return text.replace(/\s+/g, ' ').trim();
};

// A column every row leaves empty is dropped: narrow paper, fewer columns.
const visibleColumns = (columns, rows) => columns.filter(col => {
  if (col.exportHide) return false;
  if (!rows.length) return true;
  return rows.some(row => cellText(col, row) !== '');
});

export const safeFileName = (parts, ext) => {
  const base = parts.filter(Boolean).join(' ').replace(/[\\/:*?"<>|]+/g, ' ').replace(/\s+/g, ' ').trim();
  return `${base || 'Report'}.${ext}`;
};

const stamp = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${p(d.getDate())}-${p(d.getMonth() + 1)}-${d.getFullYear()}`;
};

// ── PDF ─────────────────────────────────────────────────────────────────────
export async function buildReportPdf({
  title, subtitle, note, columns, rows, totalsRow,
  orientation = 'landscape', organiserName, eventName,
  density = 'compact',          // compact = more rows per page; normal = easier to read
}) {
  const d = density === 'normal'
    ? { font: 8, pad: 1.6, head: 8, margin: 12, titleGap: 5 }
    : { font: 6.6, pad: 0.8, head: 6.8, margin: 7, titleGap: 4 };
  const { default: JsPDF } = await import('jspdf');
  const { default: autoTable } = await import('jspdf-autotable');

  const doc = new JsPDF({ orientation, unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const cols = visibleColumns(columns, rows);

  // Header: who it belongs to, what the list is, when it was taken.
  // One header line instead of four: name of the event, what the list is and
  // its filters on the left, when it was taken and how many rows on the right.
  const left = [eventName || organiserName || 'Mehfil', title, subtitle].filter(Boolean).join('  ·  ');
  doc.setFont('helvetica', 'bold'); doc.setFontSize(d.font + 2.4);
  doc.text(String(left), d.margin, 8);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(d.font); doc.setTextColor(90);
  doc.text(`${rows.length} ${rows.length === 1 ? 'row' : 'rows'}  ·  ${stamp()}`, pageW - d.margin, 8, { align: 'right' });
  let y = 8;
  if (note) {
    doc.setFont('helvetica', 'italic'); doc.setTextColor(120);
    doc.text(String(note), d.margin, y + 4);
    y += 4;
  }
  doc.setTextColor(0);

  const body = rows.map(row => cols.map(col => cellText(col, row)));
  if (totalsRow) body.push(cols.map(col => String(totalsRow[col.key] ?? '')));

  autoTable(doc, {
    startY: y + d.titleGap,
    head: [cols.map(col => col.label)],
    body,
    theme: 'grid',
    styles: { font: 'helvetica', fontSize: d.font, cellPadding: d.pad, overflow: 'linebreak', textColor: 20, lineWidth: 0.1 },
    headStyles: { fillColor: [232, 238, 247], textColor: 20, fontStyle: 'bold', fontSize: d.head },
    alternateRowStyles: { fillColor: [249, 250, 252] },
    columnStyles: Object.fromEntries(cols.map((col, i) => [i, {
      halign: col.align === 'num' || col.align === 'right' ? 'right' : col.align === 'center' ? 'center' : 'left',
      cellWidth: col.pdfWidth || 'auto',
    }])),
    // The totals line is the last body row: print it bold.
    didParseCell: (data) => {
      if (totalsRow && data.section === 'body' && data.row.index === body.length - 1) {
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.fillColor = [238, 242, 248];
      }
    },
    didDrawPage: () => {
      const page = doc.internal.getCurrentPageInfo().pageNumber;
      doc.setFont('helvetica', 'normal'); doc.setFontSize(d.font - 0.6); doc.setTextColor(120);
      doc.text(`${title || 'Report'} — page ${page}`, pageW / 2, doc.internal.pageSize.getHeight() - 3.5, { align: 'center' });
      doc.setTextColor(0);
    },
    margin: { left: d.margin, right: d.margin, top: 10, bottom: 7 },
  });

  return doc.output('blob');
}

// ── Excel ───────────────────────────────────────────────────────────────────
export async function buildReportExcel({
  title, subtitle, note, columns, rows, totalsRow, organiserName, eventName,
}) {
  const XLSX = await import('xlsx');
  const cols = visibleColumns(columns, rows);
  const width = cols.length;
  const pad = (first) => [first, ...Array(Math.max(width - 1, 0)).fill('')];

  const aoa = [];
  aoa.push(pad(String(eventName || organiserName || 'Mehfil')));
  if (organiserName && eventName) aoa.push(pad(String(organiserName)));
  aoa.push(pad(String(title || 'Report')));
  if (subtitle) aoa.push(pad(String(subtitle)));
  if (note) aoa.push(pad(String(note)));
  aoa.push(pad(`Printed: ${stamp()}   ·   ${rows.length} ${rows.length === 1 ? 'row' : 'rows'}`));
  aoa.push(Array(width).fill(''));

  const headerIndex = aoa.length;
  aoa.push(cols.map(col => col.label));
  rows.forEach(row => aoa.push(cols.map(col => {
    const text = cellText(col, row);
    // Keep numbers numeric so Excel can sum them; a mobile number stays text.
    if (col.align === 'num' && text && /^-?[\d,]+(\.\d+)?$/.test(text.replace(/[₹\s]/g, ''))) {
      return Number(text.replace(/[₹,\s]/g, ''));
    }
    return text;
  })));
  if (totalsRow) aoa.push(cols.map(col => totalsRow[col.key] ?? ''));

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!cols'] = cols.map(col => ({
    wch: Math.min(Math.max(String(col.label).length + 4, col.excelWidth || 14), 42),
  }));
  // The title block spans the table so it reads like a report, not a stray cell.
  ws['!merges'] = Array.from({ length: headerIndex - 1 }, (_, r) => ({
    s: { r, c: 0 }, e: { r, c: Math.max(width - 1, 0) },
  }));
  ws['!freeze'] = { xSplit: 0, ySplit: headerIndex + 1 };
  ws['!autofilter'] = {
    ref: XLSX.utils.encode_range({
      s: { r: headerIndex, c: 0 },
      e: { r: headerIndex + rows.length, c: Math.max(width - 1, 0) },
    }),
  };

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, String(title || 'Report').slice(0, 28) || 'Report');
  const out = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  return new Blob([out], { type: EXCEL_MIME });
}

// ── Where the file goes ─────────────────────────────────────────────────────
export function saveBlob(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = fileName;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

// Print: the PDF opens in a hidden frame and the browser's print dialog is
// asked for it, so what is printed is exactly what the PDF shows.
export function printPdfBlob(blob) {
  const url = URL.createObjectURL(blob);
  const frame = document.createElement('iframe');
  frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0';
  frame.src = url;
  frame.onload = () => {
    try { frame.contentWindow.focus(); frame.contentWindow.print(); } catch { window.open(url, '_blank'); }
  };
  document.body.appendChild(frame);
  setTimeout(() => { frame.remove(); URL.revokeObjectURL(url); }, 60000);
}
